import type { ProductEntity } from '@/entities/assortment'
import type { LocalStateUpdater } from '@/shared/components/data-grid/helpers'
import type { SRT_TableInstance } from '@/shared/components/master-table'
import type {
	FieldSchemaModel,
	PricingCampaignModel,
	ProductsQueryItemModel,
} from '@/shared/generated'

import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
	getEntityFieldValue,
	getNodeId,
	getPricingTypeChoices,
	getRowId,
} from '@/entities/assortment'
import {
	BasicImageField,
	createChoices,
	createColumnProcessor,
	useColumnsFields,
} from '@/entities/column'
import { useBrandsQuery, useCategoriesQuery } from '@/entities/core'
import {
	useOptimizationGroupsQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'
import {
	getAlertAnnotationDataChoices,
	usePricingAlertsAnnotationsQuery,
} from '@/entities/price-alert'
import {
	getPcBadgeVariant,
	getPricingCampaignEngine,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { useTableChangesStorage } from '@/shared/components/data-grid/helpers'
import { createSRTColumnHelper } from '@/shared/components/master-table'
import { LinkButton } from '@/shared/components/ui/link-button'
import { FieldType } from '@/shared/generated'
import { useCanEditModelInput } from '@/shared/hooks/data'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { useStore } from '@/shared/lib/datasheet/store'

export function useColumns(schema: FieldSchemaModel[]) {
	const { t } = useTranslation()

	const optimizationGroupId = useOptimizationGroupId()!
	const isDisabledEditing = !useCanEditModelInput(optimizationGroupId)
	const { data: sales_entity } = useSalesEntitiesQuery()
	const { data: category } = useCategoriesQuery({
		optimization_group_id: optimizationGroupId,
	})
	const { data: brand } = useBrandsQuery({
		optimization_group_id: optimizationGroupId,
	})
	const { data: optimization_group } = useOptimizationGroupsQuery()
	const { data: pricing_campaign } = usePricingCampaignsQuery({
		optimization_group_id: optimizationGroupId,
	})
	const { data: alert } = usePricingAlertsAnnotationsQuery({
		select: (data) => getAlertAnnotationDataChoices(data, t),
	})
	const entities = useMemo(
		() => ({
			sales_level: (sales_entity?.sales_levels || []).map((e) => ({
				...e,
				id: e.level,
			})),
			sales_entity: sales_entity?.sales_entities || [],
			category: category || [],
			brand: brand || [],
			optimization_group: optimization_group || [],
			pricing_campaign: pricing_campaign || [],
			alert: alert || [],
		}),
		[sales_entity, category, brand, optimization_group, pricing_campaign, alert]
	)
	const getChoices = useCallback(
		(schema: FieldSchemaModel[]) => {
			const getChoiceExtraParams = <T,>(field: FieldSchemaModel, choice: T) => {
				if (field.reference?.entity === 'pricing_campaign') {
					return {
						variant: getPcBadgeVariant(
							getPricingCampaignEngine(choice as PricingCampaignModel)
						),
					}
				}

				return {}
			}

			return {
				pricing_type: getPricingTypeChoices(t),
				...createChoices({
					entities,
					schema,
					getChoiceExtraParams,
				}),
			}
		},
		[entities, t]
	)
	const setLocalData = useStore((state) => state.setLocalData)
	const localData = useStore((state) => state.localData)
	const tableChangesStorage = useTableChangesStorage({
		setLocalData,
		localData,
		getId: getRowId,
		updater: ogProductCustomLocalStateUpdater,
	})
	const transformedSchema = useMemo(
		() =>
			schema.map((field) => {
				if (field.id === 'pos_ids') {
					return {
						...field,
						field_type: FieldType.Chip,
					}
				}

				return field
			}),
		[schema]
	)

	return useColumnsFields({
		schema: transformedSchema,
		getChoices,
		processor: createColumnProcessor(
			{
				columnHelper,
				enableCellEditing: () => {
					if (isDisabledEditing) {
						return t('disabled_reason.optimization_wrong_status', {
							defaultValue:
								'Editing is forbidden bacause of the optimization group status.',
						})
					}

					return true
				},
				tableChangesStorage,
				getCell,
				getAccessorFn: (schema: FieldSchemaModel) => {
					switch (schema.id) {
						case 'sku':
							return (row: ProductsQueryItemModel | ProductEntity) =>
								(row as ProductEntity).sku ??
								(row as ProductsQueryItemModel).line_id
						case 'title':
							return (row: ProductsQueryItemModel | ProductEntity) =>
								(row as ProductEntity).title ??
								(row as ProductsQueryItemModel)?.fields?.name
						default:
							return (row) => getEntityFieldValue(row, schema.id)
					}
				},
			},
			t
		),
	})
}
const ogProductCustomLocalStateUpdater: LocalStateUpdater = ({
	control,
	name,
	defaultValue,
	value,
	row,
}) => {
	const isPricingLine = Boolean((row as ProductsQueryItemModel).products)
	const products = (row as ProductsQueryItemModel).products
	const newState = { ...control.localData } as Record<string, any>
	const updateState = (id: string) => {
		if (value == defaultValue) {
			if (newState[id]) {
				delete newState[id][name]
			}
			if (newState[id] && Object.keys(newState[id]).length === 0) {
				delete newState[id]
			}
		} else {
			newState[id] = {
				...(newState[id] || {}),
				[name]: value,
			}
		}
	}
	if (isPricingLine) {
		// Set value for all products in line if it is a pricing line
		updateState(getRowId(row))
		products.forEach((product) => {
			const productId = `${getNodeId(row)}__${product.sku}`
			updateState(productId)
		})
	} else {
		// Set value for all products in line if it is not a pricing line
		updateState(`${getNodeId(row)}__`)
		updateState(getRowId(row))
	}

	return newState
}
const columnHelper = createSRTColumnHelper<
	ProductsQueryItemModel | ProductEntity
>()

const getCell = (schema: FieldSchemaModel) => {
	if (schema.id === 'sku') {
		return ProductIdentField
	}
	if (schema.id === 'title') {
		return TitleField
	}
	if (schema.id === 'image_url') {
		return BasicImageField
	}

	return null
}
const getMarkedText = (text: string, searchTextValue?: string) => {
	if (!searchTextValue) {
		return <p>{text}</p>
	}
	const searchText = (searchTextValue || '').replace(
		/[-[\]{}()*+?.,\\^$|#\s]/g,
		'\\$&'
	)
	const regExp = new RegExp(searchText, 'gi')
	const matched = text && text.match(regExp)

	return (
		<span
			dangerouslySetInnerHTML={{
				__html:
					text &&
					text.replace(regExp, `<mark>${matched?.[0] ?? searchText}</mark>`),
			}}
		/>
	)
}

function ProductIdentField({
	value,
	row,
	table,
}: {
	value: string
	row: ProductsQueryItemModel
	table: SRT_TableInstance<ProductsQueryItemModel>
}) {
	const searchText = table?.getState().searchText || ''
	const setIsSkuPanelOpen = useStore((state) => state.setIsSidebarOpen)
	const setSkuSidebarContent = useStore((state) => state.setSidebarContent)

	const onClick = () => {
		setIsSkuPanelOpen(true)
		setSkuSidebarContent(row)
	}

	return (
		<LinkButton variant='brand' onClick={onClick}>
			{getMarkedText(value, searchText)}
		</LinkButton>
	)
}

function TitleField({
	value,
	table,
}: {
	value: string
	table: SRT_TableInstance<ProductsQueryItemModel>
}) {
	const searchText = table?.getState().searchText || ''

	return <div>{getMarkedText(value, searchText)}</div>
}
